import React, { useState } from 'react';
import { getSourceDisplayName, getSourceUrl } from '../utils/sourceRegistry';
import DocViewerModal from './DocViewerModal';

/**
 * Admin view of coaching logs. Shows tech question, response, and expandable
 * "Chain of thought" (AI reasoning) plus sources.
 */
function CoachingLogViewer({ logs }) {
  const [expandedId, setExpandedId] = useState(null);
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });

  const renderMarkdown = (text) => {
    const rawText = text || '';
    try {
      if (window.marked) {
        return { __html: window.marked.parse(rawText) };
      }
    } catch (e) {
      console.error('Markdown Error', e);
    }
    return { __html: rawText };
  };

  if (logs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
        No coaching logs yet.
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
            <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
              Time
            </th>
            <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
              Ticket
            </th>
            <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
              Step / Question
            </th>
            <th style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', textAlign: 'center' }}>
              Chain of thought
            </th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, idx) => {
          const rowId = log.coaching_id || `coaching-${log.timestamp}-${idx}`;
          return (
            <React.Fragment key={rowId}>
              <tr
                onClick={() => setExpandedId(expandedId === rowId ? null : rowId)}
                style={{
                  borderBottom: '1px solid var(--border-color)',
                  cursor: 'pointer',
                  background: expandedId === rowId ? 'var(--bg-card-hover)' : 'transparent',
                }}
              >
                <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  {log.ticket_id || '—'}
                </td>
                <td style={{ padding: '10px 12px', fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{log.step_context || '—'}</span>
                  <br />
                  {log.tech_question || '—'}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <button type="button" className="btn btn--sm btn--outline" style={{ padding: '2px 8px', fontSize: 10 }}>
                    {expandedId === rowId ? 'Hide' : 'View'}
                  </button>
                </td>
              </tr>
              {expandedId === rowId && (
                <tr>
                  <td colSpan="4" style={{ padding: '16px 24px', background: 'var(--bg-card-hover)', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div>
                        <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Response</h4>
                        <div
                          style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)' }}
                          dangerouslySetInnerHTML={renderMarkdown(log.response)}
                        />
                      </div>
                      {log.reasoning && (
                        <div>
                          <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Chain of thought</h4>
                          <div
                            className="reasoning-rich-text"
                            style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}
                            dangerouslySetInnerHTML={renderMarkdown(log.reasoning)}
                          />
                        </div>
                      )}
                      {log.sources && log.sources.length > 0 && (
                        <div>
                          <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Knowledge base used (RAG)</h4>
                          <p style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>Specific documents used for this answer. Click to open:</p>
                          <ul className="rag-sources-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {log.sources.map((src, idx) => {
                              const filename = typeof src === 'string' ? src.trim() : String(src);
                              const displayName = getSourceDisplayName(filename);
                              const href = getSourceUrl(filename);
                              return (
                                <li
                                  key={idx}
                                  className="rag-source-item"
                                  style={{
                                    fontSize: 12,
                                    fontFamily: 'var(--font-mono)',
                                    padding: '4px 0',
                                    borderBottom: '1px solid var(--border-color)',
                                    color: 'var(--text-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span className="rag-source-item__name">{displayName}</span>
                                  <button type="button" className="rag-source-item__link" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: 11 }} onClick={() => setDocViewer({ open: true, url: href, title: displayName })}>View document →</button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        </tbody>
      </table>
      <DocViewerModal isOpen={docViewer.open} url={docViewer.url} title={docViewer.title} onClose={() => setDocViewer({ open: false, url: '', title: '' })} />
    </div>
  );
}

export default CoachingLogViewer;
